import { Field, connect, getIn, useFormikContext } from 'formik';
import { castArray } from 'lodash';
import PropTypes from 'prop-types';
import { useMemo } from 'react';
import { useTheme } from 'styled-components';

import { AdditionalInformation } from '@hivebrite/design-system/AdditionalInformation';
import { Box } from '@hivebrite/design-system/Box';
import { Label } from '@hivebrite/design-system/Label';
import { ScreenReadersContent } from '@hivebrite/design-system/ScreenReadersContent';
import { POSITIONS } from '@hivebrite/design-system/Tooltip';
import { getErrorId, getTooltipId } from '@hivebrite/utils';

import getSelectComponent from 'hb-react/shared/components/formik/utils/getSelectComponent';
import { getOptionValue } from 'hb-react/shared/components/ReactSelect/utils/selectValues';

import { validateSelectArray } from './validate';

const SelectArray = ({
  ariaDescribedBy,
  ariaLabel,
  creatable = false,
  disabled,
  getValue,
  hideError = false,
  id,
  info,
  infoIcon,
  inputHtml = {},
  isClearable,
  isNotSearchable,
  label,
  labelMultiline = true,
  multiple = false,
  name,
  notCloseMenuOnSelect,
  onBlur,
  onChange,
  options,
  placeholder,
  tooltip,
  type = 'default',
  validate = {},
  wrapperProps = {},
  ...otherProps
}) => {
  const {
    errors,
    values,
    touched,
    setFieldValue,
    setFieldTouched,
    submitCount,
  } = useFormikContext();
  const theme = useTheme();
  const SelectComponent = getSelectComponent(type);
  const htmlId = id || name;
  const formikValue = getIn(values, name);
  const formikError = getIn(errors, name);
  const formikTouched = getIn(touched, name);
  const { required } = validate;

  const tooltipId = useMemo(() => getTooltipId(htmlId), [htmlId]);
  const errorId = useMemo(() => getErrorId(htmlId), [htmlId]);

  const getReactSelectValue = (formikValue) => {
    if (typeof getValue === 'function') {
      return getValue(formikValue);
    }

    const arrayValue = castArray(formikValue);

    return multiple ? arrayValue : arrayValue[0] || '';
  };

  const reactSelectValue = getReactSelectValue(formikValue);

  const handleChange = (selectedOptions) => {
    let newValue;

    if (selectedOptions) {
      if (multiple) {
        newValue = selectedOptions.map((option) => getOptionValue(option));
      } else {
        newValue = [selectedOptions.value || selectedOptions.id];
      }
    } else {
      newValue = [];
    }

    setFieldValue(name, newValue.filter(Boolean));

    if (onChange) {
      onChange(selectedOptions);
    }
  };

  const handleBlur = () => {
    setFieldTouched(name, true);

    if (onBlur) {
      onBlur();
    }
  };

  const asyncScreenReadersContent = useMemo(() => {
    if (type === 'industries' || type === 'skills' || type === 'companies') {
      return ` - ${s__('Accessibility|Start typing to display suggestions')}`;
    }

    return undefined;
  }, [type]);

  return (
    <Box mb={6} display="block" {...wrapperProps}>
      <Box
        display="block"
        opacity={disabled ? theme.input.disabled.opacity : 1}
      >
        <Label
          id={`${htmlId}-input`}
          disabled={disabled}
          multiline={labelMultiline}
          required={required}
          tooltip={tooltip}
          initialPosition={POSITIONS.BOTTOM}
        >
          {label}
          {asyncScreenReadersContent && (
            <ScreenReadersContent>
              {asyncScreenReadersContent}
            </ScreenReadersContent>
          )}
        </Label>
        <Field
          name={name}
          validate={(value) =>
            validateSelectArray({
              required,
              value: Array.isArray(value) ? value : [value],
            })
          }
        >
          {() => (
            <SelectComponent
              ariaDescribedBy={ariaDescribedBy}
              // Note - describedById is not align with the rest of the App (should be ariaDescribedBy). To be updated
              {...((formikError || ariaDescribedBy || tooltip) && {
                describedById: [
                  formikError && errorId,
                  ariaDescribedBy,
                  tooltip && tooltipId,
                ]
                  .filter(Boolean)
                  .join(' '),
              })}
              ariaLabel={ariaLabel}
              creatable={creatable}
              disabled={disabled}
              error={(submitCount || formikTouched) && formikError}
              id={htmlId}
              isClearable={isClearable}
              isNotSearchable={isNotSearchable}
              isMulti={multiple}
              name={name}
              notCloseMenuOnSelect={notCloseMenuOnSelect}
              onBlur={handleBlur}
              onChange={handleChange}
              options={options}
              placeholder={placeholder}
              value={reactSelectValue}
              hideError
              {...inputHtml}
              {...otherProps}
            />
          )}
        </Field>
      </Box>

      {/* Make tooltip also available for ScreenReaders */}
      {tooltip && (
        <ScreenReadersContent id={tooltipId}>
          {typeof tooltip === 'function' ? tooltip() : tooltip}
        </ScreenReadersContent>
      )}
      {info && typeof info === 'string' && (
        <AdditionalInformation iconName={infoIcon} id={id} type="info">
          {info}
        </AdditionalInformation>
      )}
      {!hideError && (formikTouched || submitCount > 0) && formikError && (
        <AdditionalInformation type="error" id={htmlId}>
          {formikError}
        </AdditionalInformation>
      )}
    </Box>
  );
};

SelectArray.propTypes = {
  name: PropTypes.string.isRequired,
  ariaDescribedBy: PropTypes.string,
  ariaLabel: PropTypes.string,
  creatable: PropTypes.bool,
  disabled: PropTypes.bool,
  getValue: PropTypes.func,
  hideError: PropTypes.bool,
  id: PropTypes.string,
  inputHtml: PropTypes.object,
  isClearable: PropTypes.bool,
  isNotSearchable: PropTypes.bool,
  label: PropTypes.string,
  multiple: PropTypes.bool,
  notCloseMenuOnSelect: PropTypes.bool,
  onBlur: PropTypes.func,
  onChange: PropTypes.func,
  options: PropTypes.oneOfType([PropTypes.object, PropTypes.array]),
  placeholder: PropTypes.string,
  tooltip: PropTypes.string,
  type: PropTypes.oneOf([
    'companies',
    'countries',
    'default',
    'degrees',
    'field_of_studies',
    'industries',
    'job_functions',
    'nationalities',
    'schools',
    'skills',
    'sub_networks',
    'timezone',
    'users',
    'codeAPE',
    'promo_codes',
    'customizable_attribute_select_async',
  ]),
  validate: PropTypes.shape({
    required: PropTypes.bool,
  }),
  wrapperProps: PropTypes.object,
};

export default connect(({ componentRef, ...props }) => (
  <SelectArray ref={componentRef} {...props} />
));
